import React, { useEffect, useMemo, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import { useAuth } from "@/context/AuthContext";
import { toast } from "sonner";
import axios from "axios";
import {
  Briefcase, MapPin, ArrowRight, ChevronRight,
  Filter, Building2, DollarSign, Calendar,
  Lock, Sparkles, CheckCircle, Loader2,
  X, Eye, EyeOff, Zap, Shield
} from "lucide-react";
import { useMarketConfig, price as fmtPrice } from "@/utils/marketConfig";
import "@/styles/landing.css";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const PENDING_JOB_KEY = "pos_pending_job";
const JOB_PREFERENCES_KEY = "job_preferences";

function safeExternalUrl(value) {
  try {
    const parsed = new URL(String(value || ""));
    const isHttp = parsed.protocol === "https:" || parsed.protocol === "http:";
    return isHttp && parsed.hostname && !parsed.username && !parsed.password ? parsed.href : "";
  } catch {
    return "";
  }
}

function scopedStorageKey(base, scope) {
  return `${base}:${scope}`;
}

function readScopedStorage(base, scope, migrateLegacy = false) {
  try {
    const key = scopedStorageKey(base, scope);
    let raw = sessionStorage.getItem(key);
    if (!raw && migrateLegacy) {
      raw = sessionStorage.getItem(base);
      if (raw) {
        sessionStorage.setItem(key, raw);
        sessionStorage.removeItem(base);
      }
    }
    return raw ? JSON.parse(raw) : null;
  } catch {
    return null;
  }
}

function readPendingJob(scope, migrateLegacy = false) {
  const saved = readScopedStorage(PENDING_JOB_KEY, scope, migrateLegacy);
  return saved && safeExternalUrl(saved.url) ? saved : null;
}

function rememberPendingJob(job, scope) {
  const url = safeExternalUrl(job?.url);
  if (!url) return null;
  const saved = {
    job_id: job.job_id || "",
    title: job.title || "Selected job",
    company: job.company || "",
    url,
    attribution_text: job.attribution_text || "",
    attribution_url: safeExternalUrl(job.attribution_url),
  };
  try {
    sessionStorage.setItem(
      scopedStorageKey(PENDING_JOB_KEY, scope),
      JSON.stringify(saved),
    );
  } catch { /* storage unavailable */ }
  return saved;
}

const JOB_CATEGORY_QUESTION = {
  id: "job_category",
  title: "Which field interests you most?",
  subtitle: "We'll find jobs matching your skills",
  options: [
    { value: "tech", label: "Technology & IT", icon: null, desc: "Software, Data, AI/ML, DevOps" },
    { value: "marketing", label: "Marketing & Growth", icon: null, desc: "Digital, Content, SEO, Ads" },
    { value: "sales", label: "Sales & Business", icon: null, desc: "Account Management, BD" },
    { value: "operations", label: "Operations & Support", icon: null, desc: "Customer Success, Ops" },
    { value: "design", label: "Design & Creative", icon: null, desc: "UI/UX, Graphics, Video" },
    { value: "finance", label: "Finance & Analytics", icon: null, desc: "Accounting, FP&A, BI" },
  ],
};

function buildQuestions(resolvedMarketName, marketReady) {
  const marketName = marketReady ? String(resolvedMarketName || "").trim() : "";
  return [
    JOB_CATEGORY_QUESTION,
    {
      id: "location",
      title: "Where do you want to work?",
      subtitle: "The current live feed contains remote opportunities",
      // Do not expose the India safety fallback while this identity's market is
      // unresolved. A neutral loading state is rendered instead.
      options: marketName ? [
        {
          value: "remote-market",
          label: `Remote (${marketName})`,
          icon: null,
          desc: `Remote roles open to candidates in ${marketName}`,
        },
        { value: "remote-global", label: "Remote (Global)", icon: null, desc: "International opportunities" },
      ] : [],
    },
  ];
}

// Job category to search keywords mapping
const CATEGORY_KEYWORDS = {
  tech: "software developer engineer python javascript react",
  marketing: "digital marketing SEO social media content marketing",
  sales: "sales business development account manager",
  operations: "operations manager customer success support",
  design: "UI UX designer graphic designer product designer",
  finance: "financial analyst accountant business analyst",
};

function normalizeJobPreferences(value) {
  const saved = value && typeof value === "object" && !Array.isArray(value)
    ? { ...value }
    : {};
  // Existing sessions used an India-specific slug. Preserve their intent as
  // "remote in my resolved market" instead of pinning every returning user to IN.
  if (saved.location === "remote-india") saved.location = "remote-market";
  return saved;
}

function readJobPreferences(scope, migrateLegacy = false) {
  const saved = normalizeJobPreferences(
    readScopedStorage(JOB_PREFERENCES_KEY, scope, migrateLegacy),
  );
  if (saved.location === "remote-market") {
    try {
      sessionStorage.setItem(
        scopedStorageKey(JOB_PREFERENCES_KEY, scope),
        JSON.stringify(saved),
      );
    } catch { /* storage unavailable */ }
  }
  return saved;
}

function writeJobPreferences(answers, scope) {
  try {
    sessionStorage.setItem(
      scopedStorageKey(JOB_PREFERENCES_KEY, scope),
      JSON.stringify(normalizeJobPreferences(answers)),
    );
  } catch { /* storage unavailable */ }
}

function arePreferencesComplete(answers, questions) {
  return questions.every((question) => question.options.some((option) => option.value === answers?.[question.id]));
}

function firstUnansweredQuestion(answers, questions) {
  const index = questions.findIndex((question) => !question.options.some((option) => option.value === answers?.[question.id]));
  return index < 0 ? questions.length - 1 : index;
}

export default function JobOnboardingPage() {
  const navigate = useNavigate();
  const { user, loading: authLoading, register, getHeaders } = useAuth();
  const { sessionId } = useAuth();
  const { cfg: mkt, ready: mktReady } = useMarketConfig();
  const questions = useMemo(
    () => buildQuestions(mkt.name, mktReady),
    [mkt.name, mktReady],
  );
  const preferencesComplete = (answers) => arePreferencesComplete(answers, questions);
  const anonymousStorageScope = `session:${sessionId}`;
  const activeStorageScope = user?.id ? `user:${user.id}` : anonymousStorageScope;
  const rememberJobPreferences = (answers) => {
    writeJobPreferences(answers, activeStorageScope);
  };
  const passPrice = mktReady ? fmtPrice(mkt, mkt.prices.pass_annual) : "…";
  const passWasPrice = mktReady ? fmtPrice(mkt, mkt.prices.pass_annual_was) : null;

  // Flow state
  const initialPreferencesRef = useRef(null);
  if (initialPreferencesRef.current === null) {
    initialPreferencesRef.current = readJobPreferences(anonymousStorageScope, true);
  }
  const initialStorageScopeRef = useRef(anonymousStorageScope);
  const storageScopeRef = useRef(anonymousStorageScope);
  const resumePreferencesRef = useRef(false);
  const [step, setStep] = useState(() => (
    preferencesComplete(initialPreferencesRef.current) ? "loading" : "questionnaire"
  )); // questionnaire, signup, loading, results
  const [currentQuestion, setCurrentQuestion] = useState(() => firstUnansweredQuestion(initialPreferencesRef.current, questions));
  const [responses, setResponses] = useState(initialPreferencesRef.current);
  const [jobs, setJobs] = useState([]);
  const [totalJobs, setTotalJobs] = useState(0);
  const [pendingJob, setPendingJob] = useState(() => readPendingJob(anonymousStorageScope, true));

  // Signup form state
  const [signupName, setSignupName] = useState("");
  const [signupEmail, setSignupEmail] = useState("");
  const [signupPassword, setSignupPassword] = useState("");
  const [showPassword, setShowPassword] = useState(false);
  const [signupLoading, setSignupLoading] = useState(false);

  // Optional Full Career OS explainer. Checkout happens on /season-pass,
  // where the current market price and plan terms are shown before payment.
  const [showPlanModal, setShowPlanModal] = useState(false);
  const [savedResponses, setSavedResponses] = useState(initialPreferencesRef.current);

  // Handle question response
  const handleResponse = (value) => {
    const question = questions[currentQuestion];
    if (!question?.options.some((option) => option.value === value)) return;
    const newResponses = { ...responses, [question.id]: value };
    setResponses(newResponses);
    rememberJobPreferences(newResponses);

    if (currentQuestion < questions.length - 1) {
      setCurrentQuestion(prev => prev + 1);
    } else {
      // All questions answered
      resumePreferencesRef.current = true;
      setSavedResponses(newResponses);
      if (user) {
        // Already logged in — go straight to results. Existing pass holders
        // should not be shown an upgrade interstitial.
        fetchJobs(newResponses);
        if (!user.has_season_pass) setShowPlanModal(true);
      } else {
        // Not logged in — show signup step first
        setStep("signup");
      }
    }
  };

  // Handle account creation
  const handleSignup = async (e) => {
    e.preventDefault();
    if (!signupName.trim() || !signupEmail.trim() || !signupPassword.trim()) {
      toast.error("Please fill in all fields");
      return;
    }
    if (signupPassword.length < 6) {
      toast.error("Password must be at least 6 characters");
      return;
    }
    setSignupLoading(true);
    try {
      const auth = await register(
        signupEmail.trim().toLowerCase(),
        signupPassword,
        signupName.trim()
      );
      toast.success("Account created! Welcome to PlacementOS");
      await fetchJobs(savedResponses, auth?.token);
      // A selected job is the user's immediate intent. Keep it visible in the
      // results and let them explicitly continue; never open or submit it for
      // them. The plan explainer can wait until no pending job needs attention.
      if (!pendingJob && !auth?.user?.has_season_pass) setShowPlanModal(true);
    } catch (err) {
      const msg = err.response?.data?.detail || "Registration failed. Please try again.";
      toast.error(msg);
    } finally {
      setSignupLoading(false);
    }
  };

  // Fetch jobs based on responses
  const fetchJobs = async (answers, tokenOverride = "") => {
    setStep("loading");

    try {
      const categoryKeywords = CATEGORY_KEYWORDS[answers.job_category] || "";
      const location = answers.location || "";
      const keywords = categoryKeywords;

      rememberJobPreferences(answers);

      const res = await axios.get(`${API}/jobs/discover`, {
        params: { keywords, location: location || "", page: 1, page_size: 20 },
        headers: {
          ...getHeaders(),
          ...(tokenOverride ? { Authorization: `Bearer ${tokenOverride}` } : {}),
        },
      });

      setJobs(res.data.jobs || []);
      setTotalJobs(res.data.hits || 0);
      setStep("results");
    } catch (err) {
      console.error("Failed to fetch jobs:", err);
      toast.error("Failed to load jobs. Please try again.");
      setStep("results");
    }
  };

  // A guest's questionnaire follows them into the account they create/sign in
  // to, but never flows in the opposite direction on logout. Returning users
  // read only their own scoped state.
  useEffect(() => {
    const previousScope = storageScopeRef.current;
    if (previousScope === activeStorageScope) return;

    let scopedPreferences = readJobPreferences(activeStorageScope);
    let scopedPendingJob = readPendingJob(activeStorageScope);
    const adoptingGuestState = activeStorageScope.startsWith("user:")
      && previousScope.startsWith("session:");

    if (adoptingGuestState && Object.keys(scopedPreferences).length === 0) {
      const guestPreferences = readJobPreferences(previousScope);
      if (Object.keys(guestPreferences).length > 0) {
        scopedPreferences = guestPreferences;
        writeJobPreferences(scopedPreferences, activeStorageScope);
      }
    }
    if (adoptingGuestState && !scopedPendingJob) {
      scopedPendingJob = readPendingJob(previousScope);
      if (scopedPendingJob) {
        rememberPendingJob(scopedPendingJob, activeStorageScope);
      }
    }
    if (adoptingGuestState) {
      try {
        sessionStorage.removeItem(scopedStorageKey(JOB_PREFERENCES_KEY, previousScope));
        sessionStorage.removeItem(scopedStorageKey(PENDING_JOB_KEY, previousScope));
      } catch { /* storage unavailable */ }
    }

    storageScopeRef.current = activeStorageScope;
    if (!adoptingGuestState) resumePreferencesRef.current = false;
    setResponses(scopedPreferences);
    setSavedResponses(scopedPreferences);
    setPendingJob(scopedPendingJob);
    setCurrentQuestion(firstUnansweredQuestion(scopedPreferences, questions));
    if (!arePreferencesComplete(scopedPreferences, questions)) {
      setStep("questionnaire");
    }
  }, [activeStorageScope, questions]);

  useEffect(() => {
    if (authLoading || !mktReady || resumePreferencesRef.current) return;
    const saved = readJobPreferences(activeStorageScope);
    if (!preferencesComplete(saved)) return;

    resumePreferencesRef.current = true;
    setResponses(saved);
    setSavedResponses(saved);
    if (user) fetchJobs(saved);
    else setStep("signup");
  }, [activeStorageScope, authLoading, mktReady, questions, user]); // eslint-disable-line react-hooks/exhaustive-deps

  const clearPendingJob = () => {
    setPendingJob(null);
    try {
      sessionStorage.removeItem(scopedStorageKey(PENDING_JOB_KEY, activeStorageScope));
      sessionStorage.removeItem(scopedStorageKey(PENDING_JOB_KEY, initialStorageScopeRef.current));
      sessionStorage.removeItem(PENDING_JOB_KEY);
    } catch { /* storage unavailable */ }
  };

  // Browsing remains free. Signing in preserves a selected job and unlocks
  // tracking, but PlacementOS never submits an external application.
  const handleJobClick = (job) => {
    const jobUrl = safeExternalUrl(job?.url);
    if (!jobUrl) {
      toast.error("This job does not have a valid provider link");
      return;
    }
    if (!user) {
      const saved = rememberPendingJob({ ...job, url: jobUrl }, activeStorageScope);
      setPendingJob(saved);
      toast.info("Create an account to save this match and continue");
      setStep("signup");
    } else {
      window.open(jobUrl, "_blank", "noopener,noreferrer");
    }
  };

  // Progress percentage
  const progress = ((currentQuestion + 1) / questions.length) * 100;

  return (
    <div className="ap-root min-h-screen" data-testid="job-onboarding-page">

      {/* Full Career OS explainer; checkout stays on the dedicated plan page. */}
      {showPlanModal && (
        <div
          className="fixed inset-0 z-50 flex items-center justify-center p-4"
          style={{ background: "rgba(12,17,29,0.6)", backdropFilter: "blur(4px)" }}
          data-testid="plan-modal"
        >
          <div
            className="ap-card ap-rise w-full max-w-md max-h-[90vh] overflow-y-auto relative"
            style={{ borderRadius: 24 }}
            role="dialog"
            aria-modal="true"
            aria-labelledby="career-os-modal-title"
          >
            {/* Close */}
            <button
              onClick={() => setShowPlanModal(false)}
              className="absolute top-4 right-4 z-10 w-9 h-9 rounded-full grid place-items-center transition-colors"
              style={{ background: "rgba(255,255,255,.18)", color: "#fff" }}
              aria-label="Close Full Career OS details"
              data-testid="plan-modal-close"
            >
              <X className="w-5 h-5" />
            </button>

            {/* Header */}
            <div className="p-7 text-white text-center" style={{ background: "var(--ap-fire)" }}>
              <div className="text-3xl mb-2">🎯</div>
              <h2 id="career-os-modal-title" className="ap-display text-[22px] font-extrabold mb-1">Your matches are ready</h2>
              <p className="text-white/90 text-sm">Browsing jobs and following provider links is free.</p>
            </div>

            {/* Body */}
            <div className="p-7">
              {/* Price */}
              <div className="text-center mb-6">
                <div className="flex items-end justify-center gap-2 mb-1">
                  {passWasPrice && <span className="text-[var(--ap-muted)] line-through text-lg mb-1">{passWasPrice}</span>}
                  <span className="ap-display text-[44px] leading-none font-extrabold ap-grad-text">{passPrice}</span>
                </div>
                <p className="text-[var(--ap-muted)] text-sm">Full Career OS · 1 year access · one-time payment</p>
              </div>

              {/* Benefits */}
              <div className="space-y-3 mb-6">
                {[
                  { icon: "🎯", text: "More current job matches and AI-assisted job tools" },
                  { icon: "✍️", text: "Per-job Apply Kits with cover letters and job-specific resume tailoring" },
                  { icon: "🧭", text: "A personalized skill path and career companion" },
                  { icon: "📄", text: "Resume Vault editing and ATS-friendly downloads" },
                ].map((b, i) => (
                  <div key={i} className="flex items-start gap-3">
                    <span className="text-lg shrink-0">{b.icon}</span>
                    <span className="text-sm text-[var(--ap-ink-2)]">{b.text}</span>
                  </div>
                ))}
              </div>

              {/* CTA */}
              <button
                onClick={() => navigate("/season-pass")}
                className="ap-btn ap-btn-fire ap-btn-lg w-full"
                data-testid="plan-buy-btn"
              >
                <ArrowRight className="w-5 h-5" />
                View Full Career OS — {passPrice}
              </button>

              <button
                onClick={() => setShowPlanModal(false)}
                className="w-full mt-3 py-2 text-sm text-[var(--ap-muted)] hover:text-[var(--ap-ink)] transition-colors bg-transparent border-0 cursor-pointer"
              >
                Skip for now — just browse jobs
              </button>

              {/* Trust */}
              <div className="flex flex-col items-center gap-1.5 mt-4 text-xs text-[var(--ap-muted)]">
                <span className="flex items-center gap-1"><Shield className="w-3 h-3" /> Review plan details before checkout</span>
                <span>No application is submitted automatically.</span>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Signup Step */}
      {step === "signup" && (
        <div className="ap-mesh ap-grid-overlay pt-24 pb-16 px-4" data-testid="signup-step">
          <div className="ap-section" style={{ maxWidth: 560 }}>
            {/* Header */}
            <div className="text-center mb-8 ap-rise">
              <div className="w-16 h-16 rounded-2xl grid place-items-center mx-auto mb-4 text-white" style={{ background: "var(--ap-fire)" }}>
                <Sparkles className="w-8 h-8" />
              </div>
              <h1 className="ap-display ap-h2" style={{ fontSize: "clamp(28px,4vw,40px)" }}>
                Your jobs are <span className="ap-grad-text">ready!</span>
              </h1>
              <p className="text-[var(--ap-muted)] mt-3">
                Create a free account to see your personalized matches
              </p>
            </div>

            {/* What they'll get */}
            <div className="ap-card ap-rise ap-rise-1 p-4 mb-6 flex gap-6 justify-center flex-wrap" style={{ background: "var(--ap-violet-soft)", border: "none" }}>
              {[
                { icon: "🎯", label: "Personalized jobs" },
                { icon: "📊", label: "ATS Score" },
                { icon: "🔧", label: "AI Resume Fix" },
              ].map((item, i) => (
                <div key={i} className="flex items-center gap-2 text-sm font-semibold" style={{ color: "var(--ap-violet)" }}>
                  <span>{item.icon}</span>
                  <span>{item.label}</span>
                </div>
              ))}
            </div>

            {/* Form */}
            <div className="ap-card ap-rise ap-rise-2 p-8">
              <form onSubmit={handleSignup} className="space-y-4">
                <div>
                  <label className="block text-[13px] font-semibold text-[var(--ap-ink)] mb-1.5">Full Name</label>
                  <input
                    type="text"
                    value={signupName}
                    onChange={e => setSignupName(e.target.value)}
                    placeholder="Rahul Sharma"
                    required
                    className="w-full px-4 py-3 rounded-xl border border-[var(--ap-line)] bg-white text-[var(--ap-ink)] text-sm focus:outline-none focus:border-[var(--ap-violet)]"
                  />
                </div>
                <div>
                  <label className="block text-[13px] font-semibold text-[var(--ap-ink)] mb-1.5">Email Address</label>
                  <input
                    type="email"
                    value={signupEmail}
                    onChange={e => setSignupEmail(e.target.value)}
                    placeholder="rahul@email.com"
                    required
                    className="w-full px-4 py-3 rounded-xl border border-[var(--ap-line)] bg-white text-[var(--ap-ink)] text-sm focus:outline-none focus:border-[var(--ap-violet)]"
                  />
                </div>
                <div>
                  <label className="block text-[13px] font-semibold text-[var(--ap-ink)] mb-1.5">Password</label>
                  <div className="relative">
                    <input
                      type={showPassword ? "text" : "password"}
                      value={signupPassword}
                      onChange={e => setSignupPassword(e.target.value)}
                      placeholder="Min. 6 characters"
                      required
                      className="w-full px-4 py-3 pr-12 rounded-xl border border-[var(--ap-line)] bg-white text-[var(--ap-ink)] text-sm focus:outline-none focus:border-[var(--ap-violet)]"
                    />
                    <button
                      type="button"
                      onClick={() => setShowPassword(!showPassword)}
                      className="absolute right-4 top-1/2 -translate-y-1/2 text-[var(--ap-muted)] hover:text-[var(--ap-ink)] bg-transparent border-0 cursor-pointer"
                    >
                      {showPassword ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                    </button>
                  </div>
                </div>
                <button
                  type="submit"
                  disabled={signupLoading}
                  className="ap-btn ap-btn-fire ap-btn-lg w-full disabled:opacity-60 mt-2"
                  data-testid="signup-submit-btn"
                >
                  {signupLoading ? <Loader2 className="w-5 h-5 animate-spin" /> : <ArrowRight className="w-5 h-5" />}
                  {signupLoading ? "Creating Account..." : "Create Free Account & See Jobs"}
                </button>
              </form>

              <div className="mt-5 text-center text-sm text-[var(--ap-muted)]">
                Already have an account?{" "}
                <button
                  onClick={() => {
                    rememberJobPreferences(savedResponses);
                    navigate("/auth?mode=login&redirect=/find-jobs");
                  }}
                  className="font-semibold hover:underline bg-transparent border-0 cursor-pointer"
                  style={{ color: "var(--ap-violet)" }}
                >
                  Log in
                </button>
              </div>
            </div>

            {/* Trust */}
            <div className="flex justify-center gap-6 mt-6 text-sm text-[var(--ap-muted)] ap-rise ap-rise-3">
              <span className="flex items-center gap-1.5"><CheckCircle className="w-4 h-4 text-[var(--ap-green)]" /> Free forever</span>
              <span className="flex items-center gap-1.5"><CheckCircle className="w-4 h-4 text-[var(--ap-green)]" /> No credit card</span>
              <span className="flex items-center gap-1.5"><CheckCircle className="w-4 h-4 text-[var(--ap-green)]" /> No automatic applications</span>
            </div>
          </div>
        </div>
      )}

      {/* Questionnaire Section */}
      {step === "questionnaire" && (
        <div className="ap-mesh ap-grid-overlay pt-24 pb-16 px-4" data-testid="questionnaire-step">
          <div className="ap-section" style={{ maxWidth: 820 }}>
            {/* Header */}
            <div className="text-center mb-8 ap-rise">
              <span className="ap-eyebrow">Top-of-funnel · Free</span>
              <h1 className="ap-display ap-h2 mt-3" style={{ fontSize: "clamp(30px,4.5vw,46px)" }}>
                Find Your <span className="ap-grad-text">Perfect Job</span>
              </h1>
              <p className="text-[var(--ap-muted)] mt-3">
                Answer a few questions to get personalized job recommendations
              </p>
            </div>

            {/* Progress Bar */}
            <div className="mb-8 ap-rise ap-rise-1">
              <div className="flex justify-between text-xs font-medium text-[var(--ap-muted)] mb-2">
                <span>Question {currentQuestion + 1} of {questions.length}</span>
                <span>{Math.round(progress)}% Complete</span>
              </div>
              <div className="h-2.5 rounded-full overflow-hidden" style={{ background: "var(--ap-canvas-2)" }}>
                <div
                  className="h-full rounded-full"
                  style={{ width: `${progress}%`, background: "var(--ap-fire)", transition: "width .6s cubic-bezier(.2,.8,.2,1)" }}
                />
              </div>
            </div>

            {/* Question Card */}
            <div className="ap-card ap-rise ap-rise-2 p-8">
              <h2 className="ap-display text-[24px] font-extrabold text-[var(--ap-ink)] mb-2">
                {questions[currentQuestion].title}
              </h2>
              <p className="text-[var(--ap-muted)] mb-8">
                {questions[currentQuestion].subtitle}
              </p>

              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                {questions[currentQuestion].id === "location" && !mktReady && (
                  <div
                    className="md:col-span-2 flex items-center justify-center gap-2 rounded-2xl border border-[var(--ap-line)] bg-[var(--ap-canvas)] p-6 text-sm text-[var(--ap-muted)]"
                    role="status"
                    data-testid="market-location-loading"
                  >
                    <Loader2 className="w-4 h-4 animate-spin" />
                    Resolving your job market...
                  </div>
                )}
                {questions[currentQuestion].options.map((option) => {
                  const Icon = option.icon;
                  return (
                    <button
                      key={option.value}
                      onClick={() => handleResponse(option.value)}
                      className="ap-pop group p-5 rounded-2xl text-left bg-white"
                      style={{ border: "1px solid var(--ap-line)" }}
                      data-testid={`option-${option.value}`}
                    >
                      <div className="flex items-start gap-4">
                        {Icon && (
                          <div className="w-11 h-11 rounded-xl grid place-items-center shrink-0" style={{ background: "var(--ap-violet-soft)", color: "var(--ap-violet)" }}>
                            <Icon className="w-5 h-5" />
                          </div>
                        )}
                        <div className="flex-1 min-w-0">
                          <h3 className="ap-display font-bold text-[var(--ap-ink)] group-hover:text-[var(--ap-violet)] transition-colors">
                            {option.label}
                          </h3>
                          <p className="text-sm text-[var(--ap-muted)] mt-1">{option.desc}</p>
                        </div>
                        <ChevronRight className="w-5 h-5 text-[var(--ap-muted)] group-hover:text-[var(--ap-violet)] group-hover:translate-x-1 transition-all shrink-0" />
                      </div>
                    </button>
                  );
                })}
              </div>
            </div>

            {/* Stats */}
            <div className="grid grid-cols-3 gap-4 mt-8 ap-rise ap-rise-3">
              <div className="text-center">
                <div className="ap-display text-[24px] font-extrabold ap-grad-text">Fresh</div>
                <div className="text-xs font-medium text-[var(--ap-muted)] mt-1">Remote Jobs</div>
              </div>
              <div className="text-center">
                <div className="ap-display text-[24px] font-extrabold ap-grad-text">Direct</div>
                <div className="text-xs font-medium text-[var(--ap-muted)] mt-1">Apply Links</div>
              </div>
              <div className="text-center">
                <div className="ap-display text-[24px] font-extrabold ap-grad-text">Daily</div>
                <div className="text-xs font-medium text-[var(--ap-muted)] mt-1">Feed Refresh</div>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Loading State */}
      {step === "loading" && (
        <div className="ap-mesh pt-32 pb-16 px-4" data-testid="loading-step">
          <div className="ap-section text-center" style={{ maxWidth: 600 }}>
            <div className="w-20 h-20 rounded-2xl grid place-items-center mx-auto mb-6 text-white" style={{ background: "var(--ap-fire)" }}>
              <Loader2 className="w-10 h-10 animate-spin" />
            </div>
            <h2 className="ap-display text-[26px] font-extrabold text-[var(--ap-ink)] mb-3">Finding Your Perfect Jobs...</h2>
            <p className="text-[var(--ap-muted)]">
              Searching the latest remote opportunities for your role and location
            </p>
          </div>
        </div>
      )}

      {/* Results */}
      {step === "results" && (
        <div className="pt-24 pb-16 px-4" data-testid="results-step">
          <div className="ap-section" style={{ maxWidth: 1100 }}>
            {/* Header */}
            <div className="flex flex-col md:flex-row md:items-center justify-between mb-8 gap-4 ap-rise">
              <div>
                <h1 className="ap-display ap-h2" style={{ fontSize: "clamp(24px,3.5vw,38px)" }}>
                  {totalJobs.toLocaleString()} <span className="ap-grad-text">Jobs Found</span>
                </h1>
                <p className="text-[var(--ap-muted)] mt-1">
                  Based on your preferences • Updated daily
                </p>
              </div>
              <button
                onClick={() => {
                  setStep("questionnaire");
                  setCurrentQuestion(0);
                  setResponses({});
                  setSavedResponses({});
                  rememberJobPreferences({});
                }}
                className="ap-btn ap-btn-ghost ap-btn-md shrink-0"
                data-testid="change-preferences-btn"
              >
                <Filter className="w-4 h-4" />
                Change Preferences
              </button>
            </div>

            {pendingJob && safeExternalUrl(pendingJob.url) && (
              <div
                className="ap-card ap-rise p-5 mb-6 flex flex-col sm:flex-row sm:items-center gap-4"
                style={{ borderColor: "rgba(91,61,245,.35)", background: "var(--ap-violet-soft)" }}
                data-testid="pending-job-continuation"
              >
                <div className="flex-1 min-w-0">
                  <p className="ap-eyebrow mb-1">Continue where you left off</p>
                  <p className="font-bold text-[var(--ap-ink)] truncate">{pendingJob.title}</p>
                  {pendingJob.company && <p className="text-sm text-[var(--ap-muted)] truncate">{pendingJob.company}</p>}
                  {pendingJob.attribution_text && (
                    safeExternalUrl(pendingJob.attribution_url) ? (
                      <a
                        href={safeExternalUrl(pendingJob.attribution_url)}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="text-xs text-[var(--ap-muted)] underline underline-offset-2"
                      >
                        {pendingJob.attribution_text}
                      </a>
                    ) : (
                      <span className="text-xs text-[var(--ap-muted)]">{pendingJob.attribution_text}</span>
                    )
                  )}
                  <p className="text-xs text-[var(--ap-muted)] mt-1">You stay in control; PlacementOS has not submitted an application.</p>
                </div>
                <div className="flex flex-wrap gap-2 shrink-0">
                  <a
                    href={safeExternalUrl(pendingJob.url)}
                    target="_blank"
                    rel="noopener noreferrer"
                    onClick={clearPendingJob}
                    className="ap-btn ap-btn-pri ap-btn-md no-underline"
                    data-testid="continue-pending-job"
                  >
                    Continue to provider <ArrowRight className="w-4 h-4" />
                  </a>
                  <button onClick={clearPendingJob} className="ap-btn ap-btn-ghost ap-btn-md">
                    Not now
                  </button>
                </div>
              </div>
            )}

            {/* User Preferences Summary */}
            <div className="ap-card ap-rise ap-rise-1 p-4 mb-6 flex flex-wrap gap-2">
              {Object.entries(responses).map(([key, value]) => (
                <span key={key} className="ap-chip" style={{ background: "var(--ap-violet-soft)", borderColor: "transparent", color: "var(--ap-violet)", fontWeight: 600 }}>
                  {questions
                    .find((question) => question.id === key)
                    ?.options.find((option) => option.value === value)
                    ?.label || value}
                </span>
              ))}
            </div>

            {/* Job Listings */}
            <div className="space-y-4">
              {jobs.length === 0 ? (
                <div className="ap-card text-center py-14" data-testid="jobs-empty">
                  <div className="w-14 h-14 rounded-2xl grid place-items-center mx-auto mb-4" style={{ background: "var(--ap-violet-soft)" }}>
                    <Briefcase className="w-7 h-7" style={{ color: "var(--ap-violet)" }} />
                  </div>
                  <h3 className="ap-display text-[20px] font-extrabold text-[var(--ap-ink)] mb-2">No jobs found</h3>
                  <p className="text-[var(--ap-muted)] mb-5">Try adjusting your preferences</p>
                  <button
                    onClick={() => {
                      setStep("questionnaire");
                      setCurrentQuestion(0);
                      setResponses({});
                      setSavedResponses({});
                      rememberJobPreferences({});
                    }}
                    className="ap-btn ap-btn-pri ap-btn-md"
                    data-testid="start-over-btn"
                  >
                    Start Over
                  </button>
                </div>
              ) : (
                jobs.map((job, idx) => (
                  <article
                    key={job.job_id || job.url || `${job.provider || "job"}:${job.title}:${job.company}`}
                    className="ap-card ap-pop group p-6"
                    data-testid={`job-card-${idx}`}
                  >
                    <div className="flex flex-col md:flex-row md:items-start justify-between gap-4">
                      <div className="flex-1 min-w-0">
                        <div className="flex items-start gap-4">
                          <div className="w-12 h-12 rounded-xl grid place-items-center shrink-0" style={{ background: "var(--ap-violet-soft)" }}>
                            <Building2 className="w-6 h-6" style={{ color: "var(--ap-violet)" }} />
                          </div>
                          <div className="flex-1 min-w-0">
                            <h3 className="ap-display text-[18px] font-bold text-[var(--ap-ink)] group-hover:text-[var(--ap-violet)] transition-colors line-clamp-1">
                              {job.title}
                            </h3>
                            <p className="font-semibold" style={{ color: "var(--ap-violet)" }}>{job.company}</p>
                            <div className="flex flex-wrap items-center gap-3 mt-2 text-sm text-[var(--ap-muted)]">
                              {job.locations && (
                                <span className="flex items-center gap-1">
                                  <MapPin className="w-4 h-4" />
                                  {job.locations}
                                </span>
                              )}
                              {job.salary && (
                                <span className="flex items-center gap-1">
                                  <DollarSign className="w-4 h-4" />
                                  {job.salary}
                                </span>
                              )}
                              {job.date && (
                                <span className="flex items-center gap-1">
                                  <Calendar className="w-4 h-4" />
                                  {job.date}
                                </span>
                              )}
                              {job.attribution_text && (
                                safeExternalUrl(job.attribution_url) ? (
                                  <a
                                    href={safeExternalUrl(job.attribution_url)}
                                    target="_blank"
                                    rel="noopener noreferrer"
                                    onClick={(event) => event.stopPropagation()}
                                    className="underline underline-offset-2 hover:text-[var(--ap-violet)]"
                                    data-testid="job-attribution"
                                  >
                                    {job.attribution_text}
                                  </a>
                                ) : (
                                  <span data-testid="job-attribution">{job.attribution_text}</span>
                                )
                              )}
                            </div>
                          </div>
                        </div>
                        <p className="text-[var(--ap-muted)] text-sm mt-3 line-clamp-2">
                          {job.description}
                        </p>
                      </div>

                      {/* Login Gate */}
                      <div className="flex items-center gap-2 shrink-0">
                        {!user ? (
                          <button type="button" onClick={() => handleJobClick(job)} className="ap-btn ap-btn-pri ap-btn-md">
                            <Lock className="w-4 h-4" />
                            <span className="text-sm font-medium">Sign in to continue</span>
                          </button>
                        ) : (
                          <button type="button" onClick={() => handleJobClick(job)} className="ap-btn ap-btn-pri ap-btn-md">
                            <ArrowRight className="w-4 h-4" />
                            <span className="text-sm font-medium">View job</span>
                          </button>
                        )}
                      </div>
                    </div>
                  </article>
                ))
              )}
            </div>

            {/* Upgrade prompt for users who haven't purchased (replaces old sign-up CTA) */}
            {user && !user.has_season_pass && jobs.length > 0 && (
              <div className="ap-card ap-mesh mt-8 p-8 text-center overflow-hidden" data-testid="upgrade-prompt">
                <div className="w-14 h-14 rounded-2xl grid place-items-center mx-auto mb-4 text-white" style={{ background: "var(--ap-fire)" }}>
                  <Zap className="w-7 h-7" />
                </div>
                <h3 className="ap-display text-[22px] font-extrabold text-[var(--ap-ink)] mb-2">
                  Add AI tools to your job search
                </h3>
                <p className="text-[var(--ap-muted)] mb-5 max-w-sm mx-auto text-sm">
                  Use your Resume Vault and per-job Apply Kits for cover letters and tailored resumes alongside the free job feed.
                </p>
                <button
                  onClick={() => setShowPlanModal(true)}
                  className="ap-btn ap-btn-fire ap-btn-md"
                  data-testid="upgrade-prompt-btn"
                >
                  Explore Full Career OS — {passPrice}
                </button>
              </div>
            )}

            {/* Load More */}
            {user && jobs.length > 0 && totalJobs > jobs.length && (
              <div className="text-center mt-4">
                <button
                  onClick={() => {
                    const params = new URLSearchParams({
                      keywords: CATEGORY_KEYWORDS[responses.job_category] || "",
                      location: responses.location || "",
                    });
                    navigate(`/jobs?${params.toString()}`);
                  }}
                  className="ap-btn ap-btn-ghost ap-btn-md"
                  data-testid="load-more-btn"
                >
                  View All {totalJobs.toLocaleString()} Jobs
                </button>
              </div>
            )}
          </div>
        </div>
      )}
    </div>
  );
}
